import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import onClickOutside from 'react-onclickoutside';
import React from 'react';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { StatusWaterfallFilterOptions } from '@bingads-webui-campaign-react/shell-ui-common';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ListSelector } from '@bingads-webui-react/list-selector';
import { Button, Glyphicon, OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { StyledWaterfallGroupHeader } from './waterfall-group-header';
import { StyledWaterfallItem } from './waterfall-item';
import { getWaterfallFilterStyles } from '../../../styles/waterfall-filter-styles';

const entityName = {
  Campaign: 'campaign',
  AdGroup: 'adGroup',
};

function getOptions(i18n) {
  return [
    {
      value: StatusWaterfallFilterOptions.All,
      description: i18n.getString(_TL_('Enabled, Paused')),
      selectorText: i18n.getString(_TL_('All')),
    },
    {
      value: StatusWaterfallFilterOptions.AllEnabled,
      description: i18n.getString(_TL_('Enabled')),
      selectorText: i18n.getString(_TL_('All enabled')),
    },
  ];
}

class WaterfallFilter extends React.PureComponent {
  state = {
    isMenuVisible: false,
  };

  onSelect = entity => (item) => {
    this.setState({ isMenuVisible: false });

    const { onChange, selected } = this.props;

    if (selected[entity] !== item.value) {
      const update = _.defaults({ [entity]: item.value }, selected);

      onChange(update);

      if (this.props.instrumentation) {
        this.props.instrumentation.writeRawLog({
          logCategory: 'ClientTrace',
          api: 'ApplyGlobalWaterfallFilter',
          message: `${entity} value has been changed to ${item.selectorText}`,
          entryType: 'Message',
        });
      }
    }
  };

  getSelectedOption() {
    const {
      i18n,
      selected,
    } = this.props;
    const { campaign, adGroup } = selected;

    const options = getOptions(i18n);
    const campaignSelected = _.find(options, opt => opt.value === campaign);
    const adGroupSelected = _.find(options, opt => opt.value === adGroup);

    return {
      campaignSelected,
      adGroupSelected,
    };
  }

  toggle = () => {
    const isMenuVisible = !this.state.isMenuVisible;
    this.setState({ isMenuVisible });
  };

  handleClickOutside = () => {
    if (this.state.isMenuVisible) {
      this.toggle();
    }
  };

  renderDivider() {
    const { classes } = this.props;
    return (<div className={classes.divider} />);
  }

  renderWaterfallCampaignHeader = () => {
    const { i18n } = this.props;

    return (
      <StyledWaterfallGroupHeader headerText={i18n.getString(_TL_('Campaign status'))} />
    );
  };

  renderWaterfallAdGroupHeader = () => {
    const { i18n } = this.props;

    return (
      <StyledWaterfallGroupHeader headerText={i18n.getString(_TL_('Ad group status'))} />
    );
  };

  renderItem = entity => (item) => {
    const { campaignSelected, adGroupSelected } = this.getSelectedOption();
    const entitySelected = (entity === entityName.Campaign ? campaignSelected : adGroupSelected);
    const isSelected = _.isEqual(entitySelected, item);

    return (
      <StyledWaterfallItem text={item.selectorText} isSelected={isSelected} />
    );
  };

  render() {
    const {
      i18n,
      classes,
      isExpanded,
    } = this.props;
    const { isMenuVisible } = this.state;

    const options = getOptions(i18n);
    const { campaignSelected, adGroupSelected } = this.getSelectedOption();
    const { description } = campaignSelected;

    const waterFallClasses = classNames('ui-next-main-menu-waterfall-filter', classes.waterFall);
    const descriptionClasses = classNames('ui-next-main-menu-waterfall-description', classes.description);
    const toggleMenuClasses = classNames(
      'main-menu-waterfall-filter-selector',
      classes.toggleMenu,
      isMenuVisible ? 'open' : ''
    );
    const toggleButtonClasses = classNames('ui-next-main-menu-dropdown-toggle', classes.toggleButton);
    const dropdownMenuClasses = classNames('dropdown-menu', classes.dropdown);

    return (
      <div className={waterFallClasses}>
        {isExpanded &&
        <React.Fragment>
          <div className={descriptionClasses}>
            <span>
              {description}
            </span>
          </div>
          <div className={toggleMenuClasses}>
            <OverlayTrigger
              key="more-options-tooltip"
              placement="right"
              delayShow={300}
              overlay={
                <Tooltip id="more-options-tooltip-right">
                  {i18n.getString(_TL_('Filter by status'))}
                </Tooltip>
              }
            >
              <Button
                className={toggleButtonClasses}
                onClick={this.toggle}
                btnStyle="command"
                aria-label={i18n.getString(_TL_('More options'))}
                aria-haspopup
                aria-expanded={isMenuVisible}
              >
                <Glyphicon
                  glyph="ba-More"
                  className={classes.toggleGlyphicon}
                />
              </Button>
            </OverlayTrigger>
            <div className={dropdownMenuClasses} aria-hidden={!isMenuVisible}>
              <FocusLock
                onExit={this.handleClickOutside}
                focusFirstItemOnMount
                isControlledFocusLock
                isFocusLock={this.state.isMenuVisible}
              >
                <ListSelector
                  renderHeader={this.renderWaterfallCampaignHeader}
                  renderItem={this.renderItem(entityName.Campaign)}
                  onSelect={this.onSelect(entityName.Campaign)}
                  data={options}
                  selectedItem={campaignSelected}
                  enableArrowKeysSupport
                  ariaLabel={i18n.getString(_TL_('Campaign status filter'))}
                />
                {this.renderDivider()}
                <ListSelector
                  renderHeader={this.renderWaterfallAdGroupHeader}
                  renderItem={this.renderItem(entityName.AdGroup)}
                  onSelect={this.onSelect(entityName.AdGroup)}
                  data={options}
                  selectedItem={adGroupSelected}
                  enableArrowKeysSupport
                  ariaLabel={i18n.getString(_TL_('Ad group status filter'))}
                />
              </FocusLock>
            </div>
          </div>
        </React.Fragment>
        }
      </div>
    );
  }
}

WaterfallFilter.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getWaterfallFilterStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  selected: PropTypes.shape({
    campaign: PropTypes.oneOf([
      StatusWaterfallFilterOptions.AllEnabled,
      StatusWaterfallFilterOptions.All]).isRequired,
    adGroup: PropTypes.oneOf([
      StatusWaterfallFilterOptions.AllEnabled,
      StatusWaterfallFilterOptions.All]).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  instrumentation: PropTypes.shape({
    writeRawLog: PropTypes.func,
  }).isRequired,
};

export const StyledWaterfallFilter =
  withInstrumentation(withDefaultStyles(onClickOutside(WaterfallFilter), getWaterfallFilterStyles));
